import { captureMessage } from '@sentry/react';
import { validateJwt } from '../jwt/functions';
import { JitsiConferenceErrors, JitsiConnectionErrors } from '../lib-jitsi-meet';
// null — штатное завершение или действие пользователя, в sentry не отправляем
const CONFERENCE_FAILURE_LEVELS = {
    [JitsiConferenceErrors.ICE_FAILED]: 'error',
    [JitsiConferenceErrors.OFFER_ANSWER_FAILED]: 'error',
    [JitsiConferenceErrors.VIDEOBRIDGE_NOT_AVAILABLE]: 'error',
    [JitsiConferenceErrors.FOCUS_LEFT]: 'error',
    [JitsiConferenceErrors.RESERVATION_ERROR]: 'error',
    [JitsiConferenceErrors.INCOMPATIBLE_SERVER_VERSIONS]: 'error',
    [JitsiConferenceErrors.CONNECTION_ERROR]: 'error',
    [JitsiConferenceErrors.FOCUS_DISCONNECTED]: 'warning',
    [JitsiConferenceErrors.GRACEFUL_SHUTDOWN]: 'warning',
    [JitsiConferenceErrors.NOT_ALLOWED_ERROR]: 'warning',
    [JitsiConferenceErrors.CONFERENCE_ACCESS_DENIED]: 'warning',
    [JitsiConferenceErrors.CONFERENCE_MAX_USERS]: 'warning',
    // попал в лобби — штатно, но пользователи приходят с вопросом «не пускает в звонок»
    [JitsiConferenceErrors.MEMBERS_ONLY_ERROR]: 'warning',
    [JitsiConferenceErrors.CONFERENCE_DESTROYED]: null,
    [JitsiConferenceErrors.CONFERENCE_RESTARTED]: null,
    [JitsiConferenceErrors.PASSWORD_REQUIRED]: null,
    [JitsiConferenceErrors.AUTHENTICATION_REQUIRED]: null,
    [JitsiConferenceErrors.DISPLAY_NAME_REQUIRED]: null
};
const CONNECTION_FAILURE_LEVELS = {
    [JitsiConnectionErrors.OTHER_ERROR]: 'error',
    [JitsiConnectionErrors.SERVER_ERROR]: 'error',
    [JitsiConnectionErrors.CONNECTION_DROPPED_ERROR]: 'warning',
    [JitsiConnectionErrors.NOT_LIVE_ERROR]: 'warning',
    [JitsiConnectionErrors.PASSWORD_REQUIRED]: null
};
const DEVICE_TITLES = {
    camera: 'Camera error',
    microphone: 'Microphone error',
    screenshare: 'Screen sharing error'
};
/**
 * Конференция упала. Неизвестные причины отправляем как error, чтобы не пропустить новые.
 *
 * @param {string} reason - JitsiConferenceErrors.
 * @param {Array} params - Параметры ошибки из lib-jitsi-meet.
 * @returns {void}
 */
export const reportConferenceFailed = (reason, params = []) => {
    // base/conference пробрасывает CONNECTION_FAILED как CONFERENCE_FAILED, он уже отправлен
    if (reason?.startsWith('connection.')) {
        return;
    }
    const level = reason in CONFERENCE_FAILURE_LEVELS ? CONFERENCE_FAILURE_LEVELS[reason] : 'error';
    if (!level) {
        return;
    }
    captureMessage(`Conference failed: ${reason}`, {
        level,
        fingerprint: ['conference-failed', reason],
        tags: {
            'failure.kind': 'conference',
            'failure.reason': reason
        },
        extra: { params }
    });
};
/**
 * Не удалось подключиться к xmpp (или соединение окончательно потеряно).
 *
 * @param {string} reason - JitsiConnectionErrors.
 * @param {string} message - Сообщение из lib-jitsi-meet.
 * @param {Object} details - Детали ошибки из lib-jitsi-meet.
 * @returns {void}
 */
export const reportConnectionFailed = (reason, message, details) => {
    const level = reason in CONNECTION_FAILURE_LEVELS ? CONNECTION_FAILURE_LEVELS[reason] : 'error';
    if (!level) {
        return;
    }
    captureMessage(`Connection failed: ${reason}`, {
        level,
        fingerprint: ['connection-failed', reason],
        tags: {
            'failure.kind': 'connection',
            'failure.reason': reason
        },
        extra: {
            message,
            details
        }
    });
};
/**
 * Сервер не принял jwt. Сам токен не отправляем, только причины и время жизни,
 * чтобы отличить протухшую ссылку от расхождения часов на устройстве.
 *
 * @param {string} jwt - Токен.
 * @param {string} reason - JitsiConnectionErrors.
 * @param {string} message - Ответ сервера, например "Not acceptable by exp (4274 sec since...)".
 * @returns {void}
 */
export const reportJwtRejected = (jwt, reason, message) => {
    const validationErrors = validateJwt(jwt).map((e) => e.key);
    let payload;
    try {
        payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    }
    catch (e) {
        payload = undefined;
    }
    // клиентский валидатор ничего не нашёл — значит отклонил сервер, например из-за часов
    const jwtReason = validationErrors[0] ?? 'rejectedByServer';
    const nowSec = Math.floor(Date.now() / 1000);
    captureMessage(`JWT rejected: ${jwtReason}`, {
        level: 'warning',
        fingerprint: ['jwt-rejected', jwtReason],
        tags: {
            'failure.kind': 'jwt',
            'failure.reason': jwtReason
        },
        extra: {
            connectionError: reason,
            message,
            validationErrors,
            expiredSecAgo: typeof payload?.exp === 'number' ? nowSec - payload.exp : undefined,
            issuedSecAgo: typeof payload?.iat === 'number' ? nowSec - payload.iat : undefined,
            lifetimeSec: typeof payload?.exp === 'number' && typeof payload?.iat === 'number'
                ? payload.exp - payload.iat : undefined
        }
    });
};
/**
 * Собирает список устройств и пермишенов, чтобы понять, что было у пользователя.
 *
 * @param {IReduxState} state - Redux state.
 * @returns {Object}
 */
const getDevicesInfo = (state) => {
    const { availableDevices, permissions } = state['features/base/devices'] ?? {};
    const labels = (devices) => devices?.map(d => d.label || '<no label>');
    return {
        permissions,
        audioInput: labels(availableDevices?.audioInput),
        videoInput: labels(availableDevices?.videoInput)
    };
};
/**
 * Не удалось получить камеру/микрофон/шаринг экрана — пользователь увидел ошибку.
 * Всегда warning: по ним не алертим, но ищем по пользователю, когда он приходит с проблемой.
 *
 * @param {DeviceKind} kind - Какое устройство не удалось получить.
 * @param {JitsiTrackError} error - Ошибка из lib-jitsi-meet.
 * @param {IReduxState} state - Redux state, если есть.
 * @returns {void}
 */
export const reportDeviceError = (kind, error, state) => {
    const reason = error?.name ?? String(error);
    captureMessage(`${DEVICE_TITLES[kind]}: ${reason}`, {
        level: 'warning',
        fingerprint: ['device-error', kind, reason],
        tags: {
            'failure.kind': 'device',
            'device.kind': kind,
            'failure.reason': reason
        },
        extra: {
            message: error?.message,
            gum: error?.gum && {
                constraints: error.gum.constraints,
                devices: error.gum.devices,
                originalError: error.gum.error && `${error.gum.error.name}: ${error.gum.error.message}`
            },
            devices: state && getDevicesInfo(state)
        }
    });
};
/**
 * Нас выкинули из конференции — не ошибка, но пользователи приходят с вопросом «меня выкинуло».
 *
 * @param {string} kickerId - Id участника, который кикнул.
 * @returns {void}
 */
export const reportKickedOut = (kickerId) => {
    captureMessage('Kicked out of conference', {
        level: 'info',
        fingerprint: ['kicked-out'],
        tags: {
            'failure.kind': 'session',
            'failure.reason': 'kicked'
        },
        extra: { kickerId }
    });
};
/**
 * Не удалось воспроизвести звук собеседника после всех ретраев — пользователь его не слышит.
 *
 * @param {Error} error - Ошибка play().
 * @returns {void}
 */
export const reportAudioPlaybackFailed = (error) => {
    const reason = error?.name ?? String(error);
    captureMessage(`Remote audio playback failed: ${reason}`, {
        level: 'warning',
        fingerprint: ['audio-playback-failed', reason],
        tags: {
            'failure.kind': 'media',
            'failure.reason': reason
        },
        extra: { message: error?.message }
    });
};
